import type { Editor, NodeEntry } from 'slate';

import type { ParagraphElement, TableElement, TableRowElement } from '../../internal';
import { parseAnchorId, Queries } from '../../internal';

/**
 * Resolves a structured-edit locationAnchor to the paragraph entry carrying
 * that locationId. Throws when no paragraph matches — every redline transform
 * treats an unresolvable anchor as an invalid suggestion.
 */
export function resolveParagraphEntryByLocationAnchor(
  editor: Editor,
  locationAnchor: string
): NodeEntry<ParagraphElement> {
  const paragraphEntry = Queries.getParagraphByLocationId(editor, locationAnchor);
  if (!paragraphEntry) {
    throw new Error(`Paragraph with locationId '${locationAnchor}' not found`);
  }
  return paragraphEntry;
}

/**
 * Resolves a `table-…` anchor id (as emitted in the LLM-facing document
 * format) to the table entry carrying that locationId. Throws on an id without
 * the `table-` prefix (a paragraph or row id where a table id was required)
 * and on an id that matches no table.
 */
export function resolveTableEntryByAnchorId(
  editor: Editor,
  anchorId: string
): NodeEntry<TableElement> {
  const parsed = parseAnchorId(anchorId);
  if (parsed.kind !== 'table') {
    throw new Error(
      `Anchor id '${anchorId}' is not a table id — expected the 'table-' prefixed id from the <table> element`
    );
  }
  const tableEntry = Queries.getTableByLocationId(editor, parsed.locationAnchor);
  if (!tableEntry) {
    throw new Error(`Table with locationId '${parsed.locationAnchor}' not found`);
  }
  return tableEntry;
}

/**
 * Resolves a `row-…` anchor id (as emitted in the LLM-facing document format)
 * to the table-row entry carrying that locationId. Throws on an id without the
 * `row-` prefix (a paragraph or table id where a row id was required) and on
 * an id that matches no row.
 */
export function resolveTableRowEntryByAnchorId(
  editor: Editor,
  anchorId: string
): NodeEntry<TableRowElement> {
  const parsed = parseAnchorId(anchorId);
  if (parsed.kind !== 'row') {
    throw new Error(
      `Anchor id '${anchorId}' is not a table-row id — expected the 'row-' prefixed id from the <tr> element`
    );
  }
  const rowEntry = Queries.getTableRowByLocationId(editor, parsed.locationAnchor);
  if (!rowEntry) {
    throw new Error(`Table row with locationId '${parsed.locationAnchor}' not found`);
  }
  return rowEntry;
}
